// Import Dependencies
import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { withRouter, Redirect } from 'react-router-dom';

// Import Components
import HeaderGroup from '~/common/components/HeaderGroup';
import Stepper from '~/common/components/Stepper';
import PricingCardList from '~/views/Pricing/components/PricingCardList';
import FeaturesChecklist from '~/views/Pricing/components/FeaturesChecklist';
import FeaturedIn from '~/views/Pricing/components/FeaturedIn';
import Faq from '~/views/Pricing/components/FAQ';
import UserCountError from '~/views/Pricing/components/UserCountError';
import Button from '~/common/components/CommonButton';
import Carousel from '~/common/components/Carousel';
import TrialInfoBlock from '~/views/Pricing/components/TrialInfoBlock';

import removeURLParameter from '~/common/utils/removeUrlParam.js';
import CarouselContent from '~/common/utils/carouselContent';
import { ghToken } from '~/common/globals.js';
import {
    FLOW_STEP_PLANS, PATH_TRIAL_LOGIN,
    PATH_TRANSFER_CURRENT_NUMBER,
    PATH_LOCAL_SEARCH, PATH_CUSTOM_TOLL_FREE,
    PATH_TOLL_FREE, PATH_EXPIRED_TRIAL_BLOCKER,
    LMI_LOGIN_FLOW_ENABLED,
    PATH_TRIAL_SIGNUP, PATH_TRIAL_NUMBERS
} from '~/common/utils/consts.js';
import { FLOWS_ENUM } from '~/common/stores/ui/loginStore';
import { gtm, viewContentEvent, clickCtaEvent } from '~/common/utils/tracking';

import styles from './pricing.css';

@withRouter
@inject(
    'offerSuiteStore',
    'progressStore',
    'convertToPaidStore',
    'loginStore',
    'modalStore',
    'transferNumberStore',
    'customTollFreeNumbersStore',
    'localNumbersStore',
    'tollFreeNumbersStore',
    'lockNumberStore',
    'promoCodeStore'
)
@observer
export default class Plans extends Component {
    componentDidMount() {
        const { history, progressStore, convertToPaidStore, transferNumberStore, customTollFreeNumbersStore, localNumbersStore, tollFreeNumbersStore, loginStore } = this.props;
        const { pathname, search } = history.location;
        const { preselectedTransferNeeded } = transferNumberStore;
        const { preselectedCustomTollFreeIntent } = customTollFreeNumbersStore;
        const { preselectedLocal } = localNumbersStore;
        const { preselectedTollFreeIntent } = tollFreeNumbersStore;

        if (preselectedCustomTollFreeIntent) return history.push(PATH_CUSTOM_TOLL_FREE);
        if (preselectedLocal) return history.push({ pathname: PATH_LOCAL_SEARCH, search });
        if (preselectedTransferNeeded && pathname !== PATH_TRIAL_LOGIN) return history.push(PATH_TRANSFER_CURRENT_NUMBER);
        if (preselectedTollFreeIntent) return history.push(PATH_TOLL_FREE);

        window.scroll({ top: 0, left: 0, behavior: 'smooth' });
        // checks if this is the app entry point and sets corresponding flag
        if (!progressStore.firstView) progressStore.setFirstView(FLOW_STEP_PLANS);
        // Opens up Convert Free Trial user's login form as a modal
        if (pathname === PATH_TRIAL_LOGIN) {
            // if auth token in the url
            if (ghToken()) {
                // save token
                loginStore.setAuthToken(ghToken());
                convertToPaidStore.getUserInfo();
                // "refresh" the page, while removing the convert param
                history.push({
                    pathname,
                    search: removeURLParameter(search, 'convert')
                });
                // if auth token is not already saved
            } else if (!loginStore.authToken.length > 0) {
                // make sure user can only log in using their email
                loginStore.setLoginByUsername(false);
                loginStore.setLogInFlow(FLOWS_ENUM.TRIAL_UPGRADE);
                // set up and open login modal
                this.observeLoginModal();
            }
        }

        gtm(viewContentEvent('plans', 'plans', 'plans'));

        return true;
    }

    observeLoginModal = () => {
        const modalFeatureType = LMI_LOGIN_FLOW_ENABLED ? 'loginLmiModal' : 'loginModal';
        if (modalFeatureType) {
            this.triggerLoginModal(modalFeatureType);
        }
    }

    triggerLoginModal = (modalFeatureType) => {
        const { modalStore } = this.props;
        modalStore.setModalOptions({
            childComponentString: modalFeatureType,
            isModalClosable: false,
            modalType: 'xlarge',
            isModalOpen: true
        });
    };

    componentWillUnmount() {
        const { offerSuiteStore } = this.props;
        // remove redirect variable if it is true
        if (offerSuiteStore.planPreselected) offerSuiteStore.setPlanPreselected(false);
    }

    renderHeader() {
        const { isUserConvertingToPaid } = this.props.convertToPaidStore;
        return (
            <h1 className={styles['hero-header']}>
                {isUserConvertingToPaid ? 'Keep the phone ringing!' : 'Choose your perfect plan'}
            </h1>
        );
    }

    renderSubheader() {
        const { loginStore, promoCodeStore } = this.props;
        const { isUserConvertingToPaid } = this.props.convertToPaidStore;
        const { promo } = promoCodeStore;
        return isUserConvertingToPaid && loginStore.userNumber ? <h2 className={styles['hero-subheader']}>Select a plan to keep using <b>{loginStore.userNumber}</b> before it gets recycled!</h2> : <h2 className={styles['hero-subheader']}>{ promo?.Message ? promo?.Message : 'Not sure which plan is right for you? Try Grasshopper free for 7 days. No credit card required.'}</h2>;
    }

    componentDidUpdate() {
        const { convertToPaidStore, modalStore } = this.props;
        const { timeoutErrorStep } = convertToPaidStore;
        if (timeoutErrorStep === 'login') {
            convertToPaidStore.setTimeoutErrorStep('');
            const modalFeatureType = LMI_LOGIN_FLOW_ENABLED ? 'loginLmiModal' : 'loginModal';
            modalStore.setModalOptions({
                childComponentString: modalFeatureType,
                isModalClosable: false,
                modalType: 'xlarge',
                isModalOpen: true
            });
        }
    }

    switchTotrial = () => {
        const {
            lockNumberStore: { numberIsLocked, isSelectedAreaCodePremium },
            history,
            modalStore
        } = this.props;
        // if number is locked
        if (numberIsLocked) {
            // if number locked is premium, show upgrade modal
            if (isSelectedAreaCodePremium) {
                modalStore.setModalOptions({
                    childComponentString: 'upgradeModal',
                    isModalClosable: true,
                    modalType: 'xlarge',
                    isModalOpen: true
                });
            } else {
                gtm(clickCtaEvent('Start a Free Trial', '/trial-signup', 'pricing plan card'));
                // redirect to trial signup form
                history.push(PATH_TRIAL_SIGNUP);
            }
        } else {
            gtm(clickCtaEvent('Start a Free Trial', '/trial-numbers', 'pricing plan card'));
            // redirect to trial numbers
            history.push(PATH_TRIAL_NUMBERS);
        }
    };


    render() {
        const {
            convertToPaidStore: { isUserConvertingToPaid, canTrialBeConverted, planWithNotEnoughUsersError, isTrialExpired },
            offerSuiteStore: { planPreselected },
            progressStore,
            history: { location: { pathname } }
        } = this.props;

        // redirect after setting the first view var, so the stepper is in the correct order
        if (planPreselected) return <Redirect to={progressStore.nextStepFromPlans} />;
        // if trial can not be converted because it is expired, redirect to expired page
        if (canTrialBeConverted === false && pathname === PATH_TRIAL_LOGIN) return <Redirect to={PATH_EXPIRED_TRIAL_BLOCKER} />;

        return (
            <main>
                <div className={`${styles.plans}`}>
                    {/* Hero Section */}
                    <div className={styles.hero}>
                        {!isUserConvertingToPaid &&
                            <Stepper
                                currentStep={FLOW_STEP_PLANS}
                                modifiers={styles['hero-nav']}
                            />
                        }
                        <HeaderGroup modifiers={styles['hero-header-grp']}>
                            {this.renderHeader()}
                            {!isTrialExpired && this.renderSubheader()}
                        </HeaderGroup>
                    </div>

                    {planWithNotEnoughUsersError ?
                        <UserCountError />
                        :
                        <PricingCardList />
                    }

                    <div className={styles['hero-text-block']}>
                        <p>Not sure which plan is right for you? Try Grasshopper free for 7 days. No credit card required.</p>
                        {
                            // hide for convert to paid and international users and trial expired
                            !isUserConvertingToPaid && !isTrialExpired &&
                                <Button
                                    modifier={styles['hero-trial-cta']}
                                    onClick={this.switchTotrial}
                                >Start a Free Trial</Button>
                        }
                    </div>


                </div>
                <FeaturesChecklist onTrialSelect={this.switchTotrial} />
                <Carousel
                    carouselData={CarouselContent}
                    header='Over 400,000 happy customers served.'
                    subHeader="Here's what a few of them have to say about Grasshopper."
                />
                <FeaturedIn />
                <Faq />
                {/* hide for convert to paid and international users and trial expired */}
                {!isUserConvertingToPaid && !isTrialExpired && <TrialInfoBlock switchTotrial={this.switchTotrial} />}
            </main>
        );
    }
}
